import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send,
  Building2,
  Clock,
  CheckCircle2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    organization: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      if (!response.ok) {
        throw new Error('Failed to submit');
      }

      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      console.error('Contact error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center py-16 px-6">
        <Card className="w-full max-w-md border-slate-100">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">Message Sent!</h1>
            <p className="text-slate-600 mb-6">
              Thank you for your inquiry. Our team will respond within 24-48 hours.
            </p>
            <Button 
              onClick={() => {
                setSubmitted(false);
                setForm({ name: '', email: '', organization: '', subject: '', message: '' });
              }}
              variant="outline"
            >
              Send Another Message
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO 
        title="Contact Us"
        description="Contact Genora Peptides for research peptide inquiries, bulk orders, and institutional accounts. Our team responds within 24-48 hours."
        keywords="contact peptide supplier, research peptide inquiry, bulk peptide order"
      />
      {/* Header */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Contact Us
          </h1>
          <p className="text-slate-600 max-w-2xl">
            Have questions about our research peptides or need assistance with your order? 
            Our team is here to help.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="space-y-6">
            <Card className="border-slate-100">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-5 w-5 text-teal-700" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-slate-900 mb-1">Email</h3>
                    <p className="text-sm text-slate-600">research@genorapeptides.com</p>
                    <p className="text-xs text-slate-400 mt-1">Response within 24-48 hours</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-100">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                    <Clock className="h-5 w-5 text-teal-700" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-slate-900 mb-1">Business Hours</h3>
                    <p className="text-sm text-slate-600">Monday - Friday</p>
                    <p className="text-sm text-slate-600">9:00 AM - 5:00 PM EST</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-100">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-5 w-5 text-teal-700" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-slate-900 mb-1">Research Inquiries</h3>
                    <p className="text-sm text-slate-600">Bulk orders and institutional accounts available</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-slate-100">
              <CardContent className="p-8">
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-6">Send a Message</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="Dr. Jane Smith"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        required
                        data-testid="contact-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="researcher@institution.edu"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        required
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="organization">Organization / Institution</Label>
                    <Input
                      id="organization"
                      type="text"
                      placeholder="University / Research Lab"
                      value={form.organization}
                      onChange={(e) => setForm({ ...form, organization: e.target.value })}
                      data-testid="contact-organization"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject *</Label>
                    <Input
                      id="subject"
                      type="text"
                      placeholder="Product inquiry, order question, etc."
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      required
                      data-testid="contact-subject"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      placeholder="Please provide details about your inquiry..."
                      rows={5}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      required
                      data-testid="contact-message"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full sm:w-auto bg-teal-700 hover:bg-teal-800 text-white h-11 px-8"
                    disabled={loading}
                    data-testid="contact-submit"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                        Sending...
                      </span>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
